import { TextInput, TextInputProps, Platform, StyleSheet } from "react-native";
import styles from "../../../stylesheet";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { TextArea, useTheme } from "tamagui";
import * as Animatable from "react-native-animatable";
import { blurPreventionState } from "@utils/blurPrevention";
import { useSelector } from "react-redux";
import { DefaultRootState } from "@reduxLocal/persistState";

// Helper function to safely merge styles for web compatibility
// This prevents indexed property errors on web by ensuring styles are merged into plain objects
const mergeStyles = (...styles: any[]): any => {
  // Filter out null/undefined/false styles
  const validStyles = styles.filter((style): style is object => {
    return style != null && style !== false && typeof style === 'object';
  });

  if (validStyles.length === 0) {
    return {};
  }

  if (Platform.OS === 'web') {
    // For web, ALWAYS merge all styles into a single plain object to avoid indexed property issues
    const merged: Record<string, any> = {};

    const processStyle = (style: any) => {
      if (!style || typeof style !== 'object') return;

      if (Array.isArray(style)) {
        // If it's an array, process each element
        style.forEach(processStyle);
      } else {
        // For objects (including StyleSheet objects), copy all enumerable properties
        for (const key in style) {
          if (Object.prototype.hasOwnProperty.call(style, key) && !/^\d+$/.test(key)) {
            // Only copy non-numeric keys to avoid indexed properties
            merged[key] = style[key];
          }
        }
      }
    };

    validStyles.forEach(processStyle);
    return merged;
  }

  // For native, return array if multiple styles, single object if one
  return validStyles.length === 1 ? validStyles[0] : validStyles;
};

export type StyledTextHandle = {
  /**
   * 
   * @property focus - Focuses the input field
   */
  focus: () => void;
};

type StyledInputFieldProps = {
  minHeight?: number;
  placeholderStyle?: any;
  /** Set to 0 to avoid shifting the field up (prevents top clipping in tight headers). Default -10. */
  layoutTopNudge?: number;
  /** Skip global `inputOutline` (margin, maxHeight 55, maxWidth) — use for feed search / tight layouts. */
  skipInputOutline?: boolean;
  /** Pass unstyled to the underlying Tamagui TextArea for full styling control. */
  unstyled?: boolean;
  /** Passed through to the underlying `TextInput` / Tamagui `TextArea` (e.g. visible caret over transparent text). */
  caretColor?: string;
}


const InputFieldStyled = forwardRef<StyledTextHandle, TextInputProps & StyledInputFieldProps>((props, ref) => {
  const { 
    multiline, 
    maxLength, 
    placeholder, 
    minHeight: propMinHeight, 
    onFocus, 
    onBlur, 
    style, 
    placeholderStyle, 
    layoutTopNudge = 0, 
    skipInputOutline = false, 
    unstyled = false,
    caretColor, 
    ...rest 
  } = props;
  const theme = themeSelector();
  const tamaguiTheme = useTheme();

  // Use a default minHeight if multiline is true and no minHeight is provided
  const minHeight = propMinHeight || (multiline ? 80 : undefined);
  const inputRef = useRef<TextInput>(null);

  // On web, Redux lightTheme might not sync with system theme, leading to "opposite" colors.
  // We resolve the effective theme by checking Tamagui's theme name which is synced in navigation/index.tsx.
  const effectiveIsLight = Platform.OS === 'web'
    ? (tamaguiTheme.name as any) === 'light'
    : theme;

  // Touch device detection and blur prevention for web
  const focusTimeRef = useRef<number>(0);
  const isTouchDeviceRef = useRef<boolean>(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const getCurrentTime = () => {
    if (Platform.OS === 'web' && typeof performance !== 'undefined' && performance.now) {
      return performance.now();
    }
    return Date.now();
  };

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
    return () => {
      // Cleanup
      inputRef.current = null;
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    focus: () => {
      console.log('focusing')
      // Expose the focus method
      inputRef.current?.focus();
    },
  }));

  
  // Also set focus time on mouse down/touch start to catch it even earlier
  // This ensures we have the timestamp BEFORE the focus event fires
  const handleMouseDown = (e: any) => {
    if (Platform.OS === 'web' && isTouchDeviceRef.current) {
      focusTimeRef.current = getCurrentTime();
    }
  };

  // Enhanced blur prevention for web touch devices
  const handleFocus = (e: any) => {
    // Set focus time IMMEDIATELY and synchronously before any blur can occur
    // Use performance.now() for more accurate timing
    if (Platform.OS === 'web') {
      focusTimeRef.current = getCurrentTime();
      // Start blur prevention proactively on focus for web touch devices
      // This prevents validation from running during the keyboard appearance transition
      if (isTouchDeviceRef.current) {
        blurPreventionState.startPrevention();
      }
    }
    // Call original onFocus handler
    onFocus?.(e);
  };

  const handleBlur = (e: any) => {
    // On web touch devices, detect false blurs from keyboard appearance
    if (Platform.OS === 'web' && isTouchDeviceRef.current) {
      const now = getCurrentTime();
      const timeSinceFocus = now - focusTimeRef.current;
      // If blur happens within 1000ms of focus, it's likely a false blur from keyboard appearing
      if (timeSinceFocus < 1000) {
        // Start blur prevention to block validation and form remounting
        blurPreventionState.startPrevention();
        // Refocus the input to restore focus that was lost due to keyboard appearance
        // Use requestAnimationFrame to refocus after the browser has finished processing the blur
        requestAnimationFrame(() => {
          // Small delay to ensure the blur event has fully processed
          setTimeout(() => {
            if (inputRef.current) {
              try {
                inputRef.current.focus();
              } catch (err) {
                console.warn('Failed to refocus input after false blur:', err);
              }
            }
          }, 10);
        });
        // Don't call onBlur to prevent validation from running
        return;
      }
    }
    // Clear blur prevention if it was active
    blurPreventionState.stopPrevention();
    // Only call original onBlur if it's a legitimate blur event
    onBlur?.(e);
  };

  return (
    <Animatable.View
      transition={'minHeight'}
      style={mergeStyles(
        { minHeight, flex: 1, alignSelf: 'stretch', overflow: 'visible' },
        layoutTopNudge !== 0 ? { top: layoutTopNudge } : {},
      )}
      {...({ 
        onMouseDown: handleMouseDown, 
        onTouchStart: handleMouseDown 
      } as any)}
    >
      <TextArea
    key={"inputOfText"}
      ref={inputRef}  // Applying the ref separately
      maxLength={maxLength || undefined}
      placeholder={placeholder || ''}
        onFocus={handleFocus}
        onBlur={handleBlur}
        blurOnSubmit={false} // Prevent blur on submit to keep keyboard open on web touch devices
        unstyled={unstyled}
        borderWidth={0}
        backgroundColor="transparent"
        borderColor="transparent"
        outlineWidth={0}
        style={mergeStyles(
          skipInputOutline
            ? {}
            : styles.inputOutline,
          {
            borderColor: "transparent",
            flex: 1,
            alignSelf: 'stretch',
            minWidth: '93%',
            minHeight: minHeight || "100%",
            color: effectiveIsLight ? '#1e293b' : '#f1f5f9',
            backgroundColor: 'transparent',
            borderWidth: 0,
            borderBottomWidth: 0,
            borderLeftWidth: 0,
            borderRightWidth: 0,
            borderTopWidth: 0,
            overflow: 'visible',
            textAlignVertical: multiline ? 'top' : 'center',
            ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
            // Override restrictive maxHeight from inputOutline if we have a larger minHeight or are multiline
            ...((multiline || propMinHeight) ? { maxHeight: undefined } : {}),
            ...(skipInputOutline ? { margin: 0, maxHeight: undefined, maxWidth: undefined } : {}),
          },
          placeholderStyle, // Apply placeholderStyle as main style if present
          style
        )}
        color={(StyleSheet.flatten(style) as any)?.color || (effectiveIsLight ? '#1e293b' : '#f1f5f9')}
        placeholderTextColor={effectiveIsLight ? '#64748b' : '#94a3b8'}
      multiline={multiline}
      {...(caretColor != null ? { caretColor } : {})}
      {...rest}
    />
    </Animatable.View>
  );
});

export default InputFieldStyled;
